'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { sendGTMEvent } from '@next/third-parties/google';
import { isEmbedPath } from '@/utils/isEmbedPath';

const PROPERTY_ID = '63166ea454f06e12d892e489';
const WIDGET_ID = '1gc7q0d1m';
const SCRIPT_ID = 'tawk-to';
// Max wait for an idle period after window load before injecting anyway.
const IDLE_TIMEOUT_MS = 3000;

type TawkPrechatField = { label?: string; answer?: string; value?: string };

declare global {
  interface Window {
    Tawk_API?: {
      onPrechatSubmit?: (data: TawkPrechatField[]) => void;
      onOfflineSubmit?: (data: Record<string, unknown>) => void;
      [key: string]: unknown;
    };
  }
}

export default function TawkChat() {
  const pathname = usePathname();

  useEffect(() => {
    window.Tawk_API = window.Tawk_API || {};

    window.Tawk_API.onPrechatSubmit = (data) => {
      sendGTMEvent({
        event: 'tawk_prechat_submit',
        page_path: window.location.pathname,
        fields: Array.isArray(data)
          ? data.map((f) => f?.label).filter(Boolean)
          : undefined,
      });
    };

    window.Tawk_API.onOfflineSubmit = (data) => {
      sendGTMEvent({
        event: 'tawk_offline_submit',
        page_path: window.location.pathname,
        fields: data ? Object.keys(data) : undefined,
      });
    };
  }, []);

  // Inject the embed once the browser is idle — but with a timeout. Next's
  // `lazyOnload` uses requestIdleCallback with NO timeout, and our pages run
  // continuous animation loops that keep the main thread from ever going idle,
  // so the widget silently never loaded.
  useEffect(() => {
    // No chat widget inside third-party iframe embeds.
    if (isEmbedPath(pathname)) return;
    if (document.getElementById(SCRIPT_ID)) return;

    const inject = () => {
      if (document.getElementById(SCRIPT_ID)) return;
      (window as unknown as { Tawk_LoadStart?: Date }).Tawk_LoadStart = new Date();
      const s = document.createElement('script');
      s.id = SCRIPT_ID;
      s.async = true;
      s.src = `https://embed.tawk.to/${PROPERTY_ID}/${WIDGET_ID}`;
      s.charset = 'UTF-8';
      s.setAttribute('crossorigin', '*');
      document.body.appendChild(s);
    };

    let idleId: number | undefined;
    let timeoutId: number | undefined;
    const schedule = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(inject, { timeout: IDLE_TIMEOUT_MS });
      } else {
        timeoutId = window.setTimeout(inject, 1);
      }
    };

    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });

    return () => {
      window.removeEventListener('load', schedule);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [pathname]);

  return null;
}
